/**
 * KAN-758: чистые помощники периода CRM для одного поля выбора дат:
 * арифметика дней ГГГГ-ММ-ДД (без часовых поясов, через UTC), подпись
 * периода ("9 октября", "1 - 9 октября"), быстрые периоды и сетка месяца
 * календаря. Лимит длины периода совпадает с сервером (366 дней включительно).
 */
import type { CrmRange } from './crmTable';

/** Наибольшая длина периода в днях, включая оба края (как MAX_RANGE_DAYS сервера). */
export const MAX_CRM_RANGE_DAYS = 366;

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

export const MONTHS_NOMINATIVE = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

export const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const DAY_MS = 86_400_000;

function toUtc(day: string): number {
  return Date.parse(`${day}T00:00:00Z`);
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** День со сдвигом на ``n`` дней (может быть отрицательным). */
export function addDays(day: string, n: number): string {
  return fromUtc(toUtc(day) + n * DAY_MS);
}

/** Число дней периода, включая оба края. */
export function rangeLength(from: string, to: string): number {
  return Math.round((toUtc(to) - toUtc(from)) / DAY_MS) + 1;
}

/** Период не длиннее лимита: лишнее срезается с начала (конец сохраняется). */
export function clampCrmRange(range: CrmRange): CrmRange {
  if (rangeLength(range.from, range.to) <= MAX_CRM_RANGE_DAYS) return range;
  return { from: addDays(range.to, -(MAX_CRM_RANGE_DAYS - 1)), to: range.to };
}

function parts(day: string): { year: number; month: number; day: number } {
  const [y, m, d] = day.split('-').map(Number);
  return { year: y, month: m, day: d };
}

/**
 * Подпись периода. Год пишется, только если период не в текущем году
 * (по ``today``) или пересекает границу года. Разделитель - обычный дефис.
 */
export function formatCrmRange(range: CrmRange, today: string): string {
  const a = parts(range.from);
  const b = parts(range.to);
  const currentYear = parts(today).year;
  const sameYear = a.year === b.year;
  const showYear = !sameYear || b.year !== currentYear;
  const yearSuffix = (year: number) => (showYear ? ` ${year}` : '');

  if (range.from === range.to) return `${b.day} ${MONTHS_GENITIVE[b.month - 1]}${yearSuffix(b.year)}`;
  if (sameYear && a.month === b.month) {
    return `${a.day} - ${b.day} ${MONTHS_GENITIVE[b.month - 1]}${yearSuffix(b.year)}`;
  }
  const left = `${a.day} ${MONTHS_GENITIVE[a.month - 1]}${sameYear ? '' : yearSuffix(a.year)}`;
  return `${left} - ${b.day} ${MONTHS_GENITIVE[b.month - 1]}${yearSuffix(b.year)}`;
}

/** День полностью, с годом: "9 октября 2026" (подписи кнопок календаря). */
export function formatDayFull(day: string): string {
  const p = parts(day);
  return `${p.day} ${MONTHS_GENITIVE[p.month - 1]} ${p.year}`;
}

export interface CrmRangePreset {
  label: string;
  range: CrmRange;
}

/** Быстрые периоды от сегодняшнего дня по Москве. */
export function crmRangePresets(today: string): CrmRangePreset[] {
  const yesterday = addDays(today, -1);
  return [
    { label: 'Сегодня', range: { from: today, to: today } },
    { label: 'Вчера', range: { from: yesterday, to: yesterday } },
    { label: '7 дней', range: { from: addDays(today, -6), to: today } },
    { label: '30 дней', range: { from: addDays(today, -29), to: today } },
  ];
}

export interface MonthRef {
  year: number;
  /** 1..12 */
  month: number;
}

export function monthOf(day: string): MonthRef {
  const p = parts(day);
  return { year: p.year, month: p.month };
}

export function shiftMonth(ref: MonthRef, n: number): MonthRef {
  const index = ref.year * 12 + (ref.month - 1) + n;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

export function compareMonth(a: MonthRef, b: MonthRef): number {
  return a.year * 12 + a.month - (b.year * 12 + b.month);
}

/**
 * Недели месяца с понедельника: дни ГГГГ-ММ-ДД, ``null`` - пустая клетка
 * до первого и после последнего дня месяца.
 */
export function monthGrid(ref: MonthRef): (string | null)[][] {
  const pad = (n: number) => String(n).padStart(2, '0');
  const first = `${ref.year}-${pad(ref.month)}-01`;
  const lead = (new Date(toUtc(first)).getUTCDay() + 6) % 7;
  const total = new Date(Date.UTC(ref.year, ref.month, 0)).getUTCDate();
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= total; d += 1) cells.push(`${ref.year}-${pad(ref.month)}-${pad(d)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
